import { Link, Stack } from "@mui/material";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import type { CorporateAttributes } from "../types";
import { isHttpUrl } from "@/utils/link";
import { DataTable } from "./DataTable";
import { EmptyAlert } from "./EmptyAlert";
import { Fields } from "./Fields";
import { TimeSeriesChart } from "./TimeSeriesChart";
import {
  formatNumberOrDash,
  formatRatioAsPercent,
  formatYenOrDash,
} from "../utils/format";
import { byPeriodAsc, toStrList } from "../utils/normalize";
export const AttributesTab: React.FC<{
  attributes: CorporateAttributes | null;
}> = ({ attributes }) => {
  if (!attributes) return <EmptyAlert>属性・業績情報は未取得</EmptyAlert>;
  // 財務指標は古い→新しい順で時系列グラフに、表は新しい順に並べる。
  // gBiz の period は「何期前」(0=当期)なので BE の periodOffset を正として並べる。
  const financeAsc = [...attributes.financeIndexes].sort(byPeriodAsc);
  const financeLabels = financeAsc.map((f) => f.period ?? "—");
  const companyUrl = attributes.companyUrl ?? undefined;
  return (
    <Stack spacing={3}>
      <Fields
        items={[
          ["業種", toStrList(attributes.industry).join(" / ") || null],
          ["資本金", formatYenOrDash(attributes.capitalStock)],
          ["従業員数", formatNumberOrDash(attributes.employeeNumber, "人")],
          ["代表者", attributes.representativeName],
          ["設立年月日", attributes.dateOfEstablishment],
          ["創業年", attributes.foundingYear],
          ["事業概要", attributes.businessSummary],
          [
            "企業サイト",
            isHttpUrl(companyUrl) ? (
              <Link href={companyUrl} target="_blank" rel="noopener noreferrer">
                {companyUrl}
              </Link>
            ) : (
              attributes.companyUrl
            ),
          ],
        ]}
      />
      <DataTable
        icon={<ShowChartOutlinedIcon />}
        title="財務指標（gBizINFO）"
        headers={[
          "決算期",
          "売上高（円）",
          "経常利益（円）",
          "純利益（円）",
          "純資産（円）",
          "総資産（円）",
        ]}
        rows={[...financeAsc]
          .reverse()
          .map((f) => [
            f.period,
            formatYenOrDash(f.netSales),
            formatYenOrDash(f.ordinaryIncomeLoss),
            formatYenOrDash(f.netIncomeLoss),
            formatYenOrDash(f.netAssets),
            formatYenOrDash(f.totalAssets),
          ])}
      />
      <TimeSeriesChart
        icon={<ShowChartOutlinedIcon />}
        title="財務推移（gBizINFO）"
        labels={financeLabels}
        series={[
          { label: "売上高", data: financeAsc.map((f) => f.netSales) },
          {
            label: "経常利益",
            data: financeAsc.map((f) => f.ordinaryIncomeLoss),
          },
          { label: "純利益", data: financeAsc.map((f) => f.netIncomeLoss) },
          { label: "純資産", data: financeAsc.map((f) => f.netAssets) },
          { label: "総資産", data: financeAsc.map((f) => f.totalAssets) },
        ]}
      />
      <DataTable
        icon={<GroupsOutlinedIcon />}
        title="大株主（gBizINFO）"
        headers={["名称", "保有比率"]}
        rows={attributes.majorShareholders.map((s) => [
          s.name,
          formatRatioAsPercent(s.shareholdingRatio),
        ])}
      />
    </Stack>
  );
};
